// src/components/ApprovalDashboard.tsx - FIXED: Dialog-based approval
import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Group as GroupIcon,
  Place as LocationIcon,
  AssignmentInd as AssignIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';


import type { VehicleRequest } from '../types';

interface Request {
  _id: string;
  purpose: string;
  destination: string;
  passengerCount: number;
  requestedBy: {
    firstName: string;
    lastName: string;
    department?: string;
  };
  priority: 'high' | 'medium' | 'low' | 'urgent';
  status: 'pending' | 'supervisor_approved' | 'fleet_approved' | 'rejected' | 'completed' | 'in_progress' | 'cancelled';
  category?: 'executive' | 'staff_bus' | 'technical' | 'general';
  startDateTime: string | Date;
}

interface ApprovalDashboardProps {
  requests: VehicleRequest[];
  onApprove: (id: string, type: 'supervisor' | 'fleet') => void;
  onReject: (id: string, type: 'supervisor' | 'fleet') => void;
  userRole?: 'supervisor' | 'fleet_manager' | 'super_admin';
}

const getPriorityColor = (priority: Request['priority']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (priority) {
    case 'urgent':
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'default';
  }
};

const getStatusChip = (status: Request['status']) => {
  switch (status) {
    case 'pending':
      return <Chip size="small" label="Pending Approval" color="warning" variant="outlined" />;
    case 'supervisor_approved':
      return <Chip size="small" label="Pending Vehicle Assignment" color="info" variant="outlined" />;
    case 'fleet_approved':
      return <Chip size="small" label="Approved" color="success" />;
    case 'rejected':
      return <Chip size="small" label="Rejected" color="error" />;
    case 'completed':
      return <Chip size="small" label="Completed" color="success" />;
    case 'in_progress':
      return <Chip size="small" label="In Progress" color="primary" />;
    default:
      return <Chip size="small" label={status} color="default" />;
  }
};

const ApprovalDashboard: React.FC<ApprovalDashboardProps> = ({ 
  requests,
  onApprove, 
  onReject,
  userRole = 'fleet_manager' 
}) => {
  // Separate requests by approval stage
  const pendingSupervisorRequests = requests.filter((r: Request) => r.status === 'pending');
  const pendingFleetRequests = requests.filter((r: Request) => r.status === 'supervisor_approved');

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Approval Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Pending Initial Approval
              </Typography>
              <Typography variant="h4">{pendingSupervisorRequests.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Pending Vehicle Assignment
              </Typography>
              <Typography variant="h4">{pendingFleetRequests.length}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Initial Approval */}
        {pendingSupervisorRequests.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pending Initial Approval
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Priority</TableCell>
                      <TableCell>Requester</TableCell>
                      <TableCell>Trip Details</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingSupervisorRequests.map((request: Request) => (
                      <TableRow
                        key={request._id}
                        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell>
                          <Chip
                            size="small"
                            color={getPriorityColor(request.priority)}
                            label={request.priority}
                            variant="filled"
                          />
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {request.requestedBy.firstName.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {request.requestedBy.firstName} {request.requestedBy.lastName}
                              </Typography>
                              {request.requestedBy.department && (
                                <Typography variant="caption" color="text.secondary">
                                  {request.requestedBy.department}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 200, fontWeight: 'bold' }}
                            >
                              {request.purpose}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {request.destination}
                              </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <GroupIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {request.passengerCount} passengers
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(request.startDateTime), 'MMM d, yyyy')}
                          </Typography>
                        </TableCell>

                        <TableCell>{getStatusChip(request.status)}</TableCell>

                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Approve Request">
                              <IconButton
                                color="success"
                                onClick={() => onApprove(request._id, 'supervisor')}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject Request">
                              <IconButton
                                color="error"
                                onClick={() => onReject(request._id, 'supervisor')}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Pending Vehicle Assignment */}
        {pendingFleetRequests.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pending Vehicle Assignment
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Priority</TableCell>
                      <TableCell>Requester</TableCell>
                      <TableCell>Trip Details</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingFleetRequests.map((request: Request) => (
                      <TableRow
                        key={request._id}
                        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell>
                          <Chip
                            size="small"
                            color={getPriorityColor(request.priority)}
                            label={request.priority}
                            variant="filled"
                          />
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {request.requestedBy.firstName.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {request.requestedBy.firstName} {request.requestedBy.lastName}
                              </Typography>
                              {request.requestedBy.department && (
                                <Typography variant="caption" color="text.secondary">
                                  {request.requestedBy.department}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 200, fontWeight: 'bold' }}
                            >
                              {request.purpose}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {request.destination}
                              </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <GroupIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {request.passengerCount} passengers
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(request.startDateTime), 'MMM d, yyyy')}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {request.category && (
                            <Chip size="small" label={request.category} variant="outlined" />
                          )}
                        </TableCell>

                        <TableCell>{getStatusChip(request.status)}</TableCell>

                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Assign Vehicle">
                              <IconButton
                                color="primary"
                                onClick={() => onApprove(request._id, 'fleet')}
                              >
                                <AssignIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject Request">
                              <IconButton
                                color="error"
                                onClick={() => onReject(request._id, 'fleet')}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Empty State */}
        {pendingSupervisorRequests.length === 0 && pendingFleetRequests.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Pending Approvals
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All vehicle requests have been processed
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ApprovalDashboard;